import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight } from "lucide-react";

export default function Cart() {
  const { t } = useLang();
  const { items, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24 min-h-screen bg-sand text-center" data-testid="cart-empty">
        <div className="max-w-md mx-auto px-6">
          <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-6" strokeWidth={1.25} />
          <h1 className="font-serif text-3xl text-navy">{t.shop.cartTitle}</h1>
          <p className="text-slate-500 mt-4">{t.shop.cartEmpty}</p>
          <Link to="/shop" className="inline-block mt-8 bg-navy text-white px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors" data-testid="cart-back-shop">
            {t.shop.backToShop}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 bg-sand min-h-screen" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
        <h1 className="font-serif text-4xl text-navy mb-2">{t.shop.cartTitle}</h1>
        <div className="divider-gold mb-10" />

        <div className="bg-white border border-slate-200">
          {items.map((it) => {
            const key = `${it.product_id}-${it.size}-${it.color}-${it.participant_name || ""}`;
            const isStage = it.type === "stage" || it.type === "event";
            return (
              <div key={key} className="flex items-center gap-4 p-5 border-b border-slate-100 last:border-b-0" data-testid={`cart-item-${it.product_id}`}>
                <img src={it.image} alt={it.name} className="w-20 h-20 object-cover bg-slate-100 shrink-0" />
                <div className="flex-1">
                  <div className="font-serif text-lg text-navy">{it.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {isStage && it.participant_name && `${t.shop.participantName}: ${it.participant_name}`}
                    {!isStage && it.size && `${t.shop.size}: ${it.size}`}
                    {!isStage && it.color && ` · ${t.shop.color}: ${it.color}`}
                  </div>
                  <div className="font-serif text-base text-navy mt-1">€ {it.unit_price.toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => updateQty(key, it.quantity - 1)} className="w-8 h-8 border border-slate-300 hover:border-navy flex items-center justify-center" data-testid={`cart-dec-${it.product_id}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-8 text-center text-sm">{it.quantity}</span>
                  <button onClick={() => updateQty(key, it.quantity + 1)} className="w-8 h-8 border border-slate-300 hover:border-navy flex items-center justify-center" data-testid={`cart-inc-${it.product_id}`}><Plus className="w-3 h-3" /></button>
                </div>
                <div className="font-serif text-lg text-navy w-24 text-right">€ {(it.unit_price * it.quantity).toFixed(2)}</div>
                <button onClick={() => removeItem(key)} className="text-slate-400 hover:text-red-600" data-testid={`cart-remove-${it.product_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          <div className="p-5 flex items-center justify-between bg-navy text-white">
            <span className="text-xs uppercase tracking-[0.25em]">{t.shop.total}</span>
            <span className="font-serif text-3xl text-club-yellow" data-testid="cart-total">€ {total.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Link to="/shop" className="inline-flex items-center justify-center gap-2 border border-navy text-navy px-7 py-3 text-xs uppercase tracking-[0.25em] hover:bg-navy hover:text-white transition-colors" data-testid="continue-shopping">
            ← {t.shop.continueShopping}
          </Link>
          <button onClick={() => navigate("/checkout")} className="flex-1 inline-flex items-center justify-center gap-2 bg-club-red text-white px-7 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark transition-colors" data-testid="goto-checkout">
            {t.shop.checkout} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
